import { Trash2, UserCog } from "lucide-react";
import { CloudinaryImageField } from "@/components/admin/CloudinaryImageField";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const compactFieldClassName =
  "h-10 w-full rounded-sm border border-[#001935]/20 bg-white px-3 py-2 text-sm text-[#001935] shadow-sm outline-none placeholder:text-[#5e6c7b] focus-visible:ring-2 focus-visible:ring-[#D4AF37]";

export const AdminUserDrawer = ({
  member,
  mode,
  onDelete,
  onFieldChange,
  onOpenChange,
  onSubmit,
  open,
  planOptions,
  roleOptions,
  subscriptionStatusOptions,
  value,
}) => {
  const isEditMode = mode === "edit";
  const currentRole = value.role || "manager";

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetContent className="w-full max-w-2xl !border-l !border-[#001935]/10 !bg-[#FDFBF7] !p-0 shadow-[-12px_0_36px_rgba(0,25,53,0.12)] sm:max-w-2xl" data-testid={isEditMode ? "edit-user-drawer" : "create-user-drawer"} side="right">
        <form className="flex h-full flex-col" data-testid={isEditMode ? "user-edit-form" : "user-create-form"} onSubmit={onSubmit}>
          <div className="border-b border-[#001935]/10 bg-white px-6 py-5">
            <SheetHeader className="space-y-0 text-left">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8b6d16]">{isEditMode ? "Edit user" : "Create backend user"}</p>
              <SheetTitle className="mt-2 text-2xl font-semibold tracking-tight text-[#001935]">
                {isEditMode ? value.name || member?.name || "User profile" : "Add new agent"}
              </SheetTitle>
              <p className="mt-2 text-sm text-[#5e6c7b]">
                {isEditMode ? member?.email : "Create a compact staff profile without leaving the user table."}
              </p>
            </SheetHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div className="grid gap-5">
              <div className="grid gap-3 md:grid-cols-2">
                <input className={compactFieldClassName} data-testid={isEditMode ? `user-name-input-${member.id}` : "user-create-name-input"} onChange={(event) => onFieldChange("name", event.target.value)} placeholder="Full name" required value={value.name || ""} />
                <input className={compactFieldClassName} data-testid={isEditMode ? `user-phone-input-${member.id}` : "user-create-phone-input"} onChange={(event) => onFieldChange("phone", event.target.value)} placeholder="Phone" value={value.phone || ""} />
              </div>

              {!isEditMode ? (
                <div className="grid gap-3 md:grid-cols-2">
                  <input className={compactFieldClassName} data-testid="user-create-email-input" onChange={(event) => onFieldChange("email", event.target.value)} placeholder="Email" required type="email" value={value.email || ""} />
                  <input className={compactFieldClassName} data-testid="user-create-password-input" onChange={(event) => onFieldChange("password", event.target.value)} placeholder="Temporary password" required type="password" value={value.password || ""} />
                </div>
              ) : null}

              <div className="grid gap-3 md:grid-cols-3">
                <select className={compactFieldClassName} data-testid={isEditMode ? `user-role-select-${member.id}` : "user-create-role-select"} onChange={(event) => onFieldChange("role", event.target.value)} value={currentRole}>
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <select className={compactFieldClassName} data-testid={isEditMode ? `user-subscription-status-select-${member.id}` : "user-create-subscription-status-select"} onChange={(event) => onFieldChange("subscription_status", event.target.value)} value={value.subscription_status || "Trial"}>
                  {subscriptionStatusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
                <select className={compactFieldClassName} data-testid={isEditMode ? `user-plan-type-select-${member.id}` : "user-create-plan-type-select"} onChange={(event) => onFieldChange("plan_type", event.target.value)} value={value.plan_type || "Digital Agent"}>
                  {planOptions.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
                </select>
              </div>

              {currentRole === "agent" ? (
                <>
                  <div className="grid gap-3 md:grid-cols-2">
                    <input className={compactFieldClassName} data-testid={isEditMode ? `user-cea-input-${member.id}` : "user-create-cea-input"} onChange={(event) => onFieldChange("cea_registration_number", event.target.value)} placeholder="CEA registration number" required value={value.cea_registration_number || ""} />
                    <input className={compactFieldClassName} data-testid={isEditMode ? `user-subdomain-slug-input-${member.id}` : "user-create-subdomain-slug-input"} onChange={(event) => onFieldChange("subdomain_slug", event.target.value)} placeholder="Profile path slug (e.g. clive)" value={value.subdomain_slug || ""} />
                  </div>

                  <CloudinaryImageField
                    compact
                    fitGuidance="Use a centered headshot with the face inside the middle safe zone so rows, drawers, and public profile cards stay clean."
                    folder="uploads/users/profile-photos"
                    helperText="Upload a profile photo"
                    label="Profile photo"
                    onChange={(nextValue) => onFieldChange("profile_photo_url", nextValue)}
                    previewAlt={`${value.name || member?.name || "User"} profile preview`}
                    testIdPrefix={isEditMode ? `user-profile-photo-${member.id}` : "user-create-profile-photo"}
                    value={value.profile_photo_url || ""}
                  />

                  <div className="grid gap-3 md:grid-cols-2">
                    <input className={compactFieldClassName} data-testid={isEditMode ? `user-team-name-input-${member.id}` : "user-create-team-name-input"} onChange={(event) => onFieldChange("team_name", event.target.value)} placeholder="Team name (optional)" value={value.team_name || ""} />
                  </div>

                  <CloudinaryImageField
                    compact
                    fitGuidance="Keep the logo centered with breathing room so the wordmark stays readable across headers and branded exports."
                    folder="uploads/users/team-logos"
                    helperText="Upload a team logo"
                    label="Team logo"
                    onChange={(nextValue) => onFieldChange("team_logo_url", nextValue)}
                    previewAlt={`${value.name || member?.name || "User"} team logo preview`}
                    previewMode="contain"
                    testIdPrefix={isEditMode ? `user-team-logo-${member.id}` : "user-create-team-logo"}
                    value={value.team_logo_url || ""}
                  />
                </>
              ) : null}
            </div>
          </div>

          <div className="border-t border-[#001935]/10 bg-white px-6 py-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                {isEditMode ? (
                  <button className="inline-flex h-10 items-center justify-center rounded-sm border border-red-200 bg-red-50 px-4 text-sm font-medium text-red-600 transition hover:bg-red-100" data-testid={`user-delete-drawer-button-${member.id}`} onClick={() => onDelete(member.id)} type="button">
                    <Trash2 className="mr-2 h-4 w-4" /> Delete User
                  </button>
                ) : <div className="hidden sm:block" aria-hidden="true" />}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row">
                <button className="inline-flex h-10 items-center justify-center rounded-sm border border-[#001935]/15 px-4 text-sm font-medium text-[#001935] transition hover:bg-[#001935]/5" data-testid={isEditMode ? `user-cancel-button-${member.id}` : "user-create-cancel-button"} onClick={() => onOpenChange(false)} type="button">
                  Cancel
                </button>
                <button className="inline-flex h-10 items-center justify-center rounded-sm bg-[#001935] px-4 text-sm font-medium text-[#FDFBF7] shadow-sm transition hover:bg-[#000d1b]" data-testid={isEditMode ? `user-save-button-${member.id}` : "user-create-submit-button"} type="submit">
                  <UserCog className="mr-2 h-4 w-4" /> {isEditMode ? "Save Changes" : "Create User"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
};